import React, { createContext, useContext, useState, useEffect, useMemo, ReactNode } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { AppContext } from 'views/App/context';
import useDTB from './_data_sources/useDTB';
import { updateDTB, removeDTBLock } from './_data_sources/manageDTB';
import { DTB } from 'views/App/Portal/DTB/_types/dtb';
import { useQueryClient } from 'react-query';
import { enqueueSnackbar } from 'notistack';
import { translate as t } from 'hooks/useTranslations';
import Error from '../../Error/index';

interface DTBLibraryContextValue {
  dtbData: DTB | null;
  editedData: DTB | null;
  setEditedData: React.Dispatch<React.SetStateAction<any>>;
  isLoading: boolean;
  isError: boolean;
  error: any;
  isSaving: boolean;
  handleSave: (shouldRedirect?: boolean, redirectTo?: string) => Promise<void>;
  selectedDtbs: DTB[];
  setSelectedDtbs: React.Dispatch<React.SetStateAction<DTB[]>>;
}

export const DTBLibraryContext = createContext<DTBLibraryContextValue>({} as DTBLibraryContextValue);

export const DTBLibraryProvider: React.FC<{ children: ReactNode; }> = ({ children }) => {
  const { id } = useParams();
  const { currentOrganizationId: orgId } = useContext(AppContext);
  const [editedData, setEditedData] = useState<DTB | null>(null);
  const [isSaving, setIsSaving] = useState<boolean>(false);
  const queryClient = useQueryClient();
  const dtbQuery = useDTB(orgId || '', id || '');
  const navigate = useNavigate();
  const [selectedDtbs, setSelectedDtbs] = useState<DTB[]>([]);

  useEffect(() => {
    if (dtbQuery.data) {
      setEditedData(dtbQuery.data);
    }
  }, [dtbQuery.data]);

  const handleSave = async (shouldRedirect: boolean = true, redirectTo: string = '/admin/dtbs/library') => {
    if (!orgId || !id || !editedData) return;
    setIsSaving(true);
    const hasInvalidQuestions = editedData?.questions?.some(
      (question) => !question.correctChoiceId || !question.question?.replace(/<[^>]*>/g, '').trim()
    );
    if (hasInvalidQuestions) {
      editedData.questions = [];
    }
    try {
      await updateDTB(orgId, id, editedData);
      enqueueSnackbar(t('dtbs.library.detail.success'), {
        variant: 'success',
        autoHideDuration: 6000,
      });
      queryClient.invalidateQueries(['dtbs', orgId]);
      if (shouldRedirect) {
        await removeDTBLock(orgId, id);
        queryClient.invalidateQueries(['dtb', orgId, id]);
        navigate(redirectTo);
      }
    } catch (err) {
      const errorMessage = t('dtbs.library.detail.error');

      let errorDetail = null;
      try {
        const errorData = JSON.parse((err as any)?.message);
        errorDetail = errorData?.detail;
      } catch (parseError) {
        console.error('Error parsing error message:', parseError);
      }

      if ((err as any).message.includes('409')) {
        errorDetail = t('dtbs.library.detail.getLockError');
      }

      const displayMessage = errorDetail || errorMessage;

      enqueueSnackbar(displayMessage, {
        variant: 'error',
        autoHideDuration: 6000,
      });
    } finally {
      setIsSaving(false);
    }
  };

  const value: DTBLibraryContextValue = useMemo(
    () => ({
      dtbData: dtbQuery.data ?? null,
      editedData,
      setEditedData,
      isLoading: dtbQuery.isFetching,
      isError: dtbQuery.isError,
      error: dtbQuery.isError ? dtbQuery : null,
      isSaving,
      handleSave,
      selectedDtbs,
      setSelectedDtbs,
    }),
    [dtbQuery.data, editedData, dtbQuery.isFetching, dtbQuery.isError, isSaving, selectedDtbs]
  );

  return <DTBLibraryContext.Provider value={value}>{children}</DTBLibraryContext.Provider>;
};

export const useDTBLibraryContext = () => useContext(DTBLibraryContext);
